import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Firestore, collection, query, where, orderBy, limit, collectionData } from '@angular/fire/firestore';
import { Title, Meta } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { FooterComponent } from '../../components/footer-main/footer-main';
import { FooterDesktopComponent } from '../../components/footer-desktop/footer-desktop';
import { GigCardComponent } from '../../components/gig-card/gig-card';
import { HeaderComponent } from '../../components/header-main/header-main';
import { HeaderDesktopComponent } from '../../components/header-desktop/header-desktop';
import { SearchClassifications } from '../../components/search-classifications/search-classifications';
import { SlugifyPipe } from '../../components/slugify.pipe';
import { UnSlugifyPipe } from '../../components/unslugify.pipe';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

// Import specific Ionic components
import { IonHeader, IonContent, IonToolbar, IonButtons, IonIcon, 
         IonTitle, IonGrid, IonRow, IonCol, IonSearchbar, 
         IonCard, IonCardHeader, IonCardTitle, IonCardContent, 
         IonCardSubtitle, IonFab, IonFabButton, IonButton, 
         IonInfiniteScroll, IonInfiniteScrollContent, IonFooter } from '@ionic/angular/standalone';

@Component({
    imports: [
        CommonModule, 
        DatePipe, 
        FooterComponent, 
        FooterDesktopComponent, 
        GigCardComponent, 
        HeaderComponent, 
        HeaderDesktopComponent, 
        SearchClassifications, 
        SlugifyPipe, 
        UnSlugifyPipe,
        // Ionic components
        IonHeader,
        IonContent,
        IonToolbar,
        IonButtons,
        IonIcon,
        IonTitle,
        IonGrid,
        IonRow,
        IonCol,
        IonSearchbar,
        IonCard,
        IonCardHeader,
        IonCardTitle,
        IonCardContent,
        IonCardSubtitle,
        IonFab,
        IonFabButton,
        IonButton,
        IonInfiniteScroll,
        IonInfiniteScrollContent,
        IonFooter
    ],
    providers: [SlugifyPipe, UnSlugifyPipe],
    selector: 'app-gigs',
    standalone: true,
    host: { ngSkipHydration: 'true' },
    styleUrls: ['./gigs.page.scss'],
    templateUrl: './gigs.page.html'
})
export class GigsPage implements OnInit {
  innerWidth: any;
  today!: number;
  @ViewChild("content", { static: true }) content: any;

  fbCollectionName: string = 'ge_gigs';
  detailUrl       : string = '/gigs';
  id              : string | undefined;
  grouping        : string | undefined;
  classification  : string | undefined;
  slug            : string | undefined;
  uid             : string | undefined;
  detail          : boolean | undefined;
  filterby        : boolean | undefined;

  items$!         : Observable<any[]>;
  locationFilter$ : BehaviorSubject<string | null> | undefined;
  countryFilter$  : BehaviorSubject<string | null> | undefined;
  regionFilter$   : BehaviorSubject<string | null> | undefined;
  groupFilter$    : BehaviorSubject<string | null> | undefined;
  classFilter$    : BehaviorSubject<string | null> | undefined;
  
  searchGrouping  : any;
  searchClass     : any;
  searchLocation  : any;
  searchCountry   : any;
  searchRegion    : any;
  searchType      : any;
  searchSalary    : any;
  searchRate      : any;
  searchValue     : any;
  searchMin       : number = 3;

  dataList        : any=[];
  filteredItems   : any=[];

  errorMessage    : boolean | undefined;
  loading         : boolean | undefined;
  infiniteScroll  : IonInfiniteScroll | undefined;

  filtersObj : any = {
    group: '',
    class: '',
    location: '',
    name: '',
    country: '',
    region: '',
    type: ''
  };

  title = 'Gig Economy Gig Jobs';
  description = 'Advertise Gig economy gigs to find & hire gig workers, service providers, contractors, consultants, freelancers, digital nomands and odd-job staff on our on-demand, local & remote worker, contingent workforce marketplace platform globally across 39 countries in Australia, Austria, Belgium, Bulgaria, Canada, Cyprus, Czech Republic, Denmark, Estonia, Finland, France, Germany, Greece, Hong Kong, Hungary, Ireland, Italy, Japan, Latvia, Lithuania, Luxembourg, Malaysia, Malta, Netherlands, New Zealand (NZ), Norway, Peru, Poland, Portugal, Romania, Singapore, Slovakia, Slovenia, Spain, Sweden, Switzerland, United Arab Emirates (UAE), United Kingdom (UK) & United States of America (USA).';

  constructor(
    public auth: Auth,
    private firestore: Firestore,
    private metaService: Meta,
    private route: ActivatedRoute,
    private router: Router,
    private slugify: SlugifyPipe,
    private titleService: Title,
    private unslugify: UnSlugifyPipe,
  ) {
  }
  ngOnInit() {
    this.grouping = this.route.snapshot.paramMap.get('grouping') ?? undefined;
    if (this.grouping) {
      this.filtersObj.group = this.unslugify.transform(this.grouping);
    }
    this.classification = this.route.snapshot.paramMap.get('classification') ?? undefined;
    if (this.classification) {
      this.filtersObj.class = this.unslugify.transform(this.classification);
    }

    this.titleService.setTitle(this.title);
    this.metaService.updateTag({ name: 'description', property: 'og:description', content: this.description })

    this.today = Date.now();
    this.searchQuery();
    if (typeof window !== 'undefined') {
      this.innerWidth = window.innerWidth;
      this.filterby = this.innerWidth >= 992;
    }
  }

  getData() {
    this.locationFilter$ = new BehaviorSubject<string | null>(null);
    this.countryFilter$ = new BehaviorSubject<string | null>(null);
    this.regionFilter$ = new BehaviorSubject<string | null>(null);
    this.groupFilter$ = new BehaviorSubject<string | null>(null);
    this.classFilter$ = new BehaviorSubject<string | null>(null);
    if (this.filtersObj.location) {
      this.locationFilter$.next(this.filtersObj.location);
    } else {
      this.locationFilter$.next(null);
    }
    if (this.filtersObj.country) {
      this.countryFilter$.next(this.filtersObj.country);
    } else {
      this.countryFilter$.next(null);
    }
    if (this.filtersObj.region) {
      this.regionFilter$.next(this.filtersObj.region);
    } else {
      this.regionFilter$.next(null);
    }
    if (this.filtersObj.group) {
      this.groupFilter$.next(this.filtersObj.group);
    } else {
      this.groupFilter$.next(null);
    }
    if (this.filtersObj.class) {
      this.classFilter$.next(this.filtersObj.class);
    } else {
      this.classFilter$.next(null);
    }
    this.items$ = combineLatest([
      this.locationFilter$,
      this.countryFilter$,
      this.regionFilter$,
      this.groupFilter$,
      this.classFilter$
    ]).pipe(
      switchMap(([location, country, region, grouping, classification]) => {
        const gigsRef = collection(this.firestore, 'ge_gigs');
        let queryConstraints: any[] = [];

        if (location === 'onsite') {
          queryConstraints.push(where('location', '==', location));
          if (country) queryConstraints.push(where('country', '==', country));
          if (region) queryConstraints.push(where('region', '==', region));
        } else if (location === 'remote') {
          queryConstraints.push(where('location', '==', location));
        }

        if (classification) {
          queryConstraints.push(where('classification', 'array-contains', classification));
        } else if (grouping) {
          queryConstraints.push(where('grouping', 'array-contains', grouping));
        }

        queryConstraints.push(where('visibility', '==', true));
        queryConstraints.push(orderBy('unpublish', 'desc'));

        const limitCount = (country && region) || (grouping && classification) ? 100 :
                         location || country || region || grouping || classification ? 250 : 100;
        queryConstraints.push(limit(limitCount));

        return collectionData(query(gigsRef, ...queryConstraints));
      })
    );
  }

  // Get search query
  searchQuery() {
    const firstParam: string | null = this.route.snapshot.queryParamMap.get('query');

    // Checks if search query parameter exist
    if (firstParam) {
      this.initItems(firstParam);
      this.searchValue = firstParam;
    } else {
      this.initItems('');
    }
  }

  // Reinitialize items
  initItems(search: string) {
    this.loading = true;
    this.getData();
    let self = this;
    this.filteredItems = [];
    this.dataList = [];
    this.items$.pipe(
      map(list => {
        list.forEach(item => {
          let itemStatus = false;
          let filter = false;

          // Filter and search empty
          if ( !filter && (search == '') ) {
            itemStatus = true;
          }

          // Filter empty search not
          if ( !filter && (search != '') ) {
            itemStatus = self.searchFilter(search, item) ?? false;
          }

          if (itemStatus) {
            self.filteredItems.push(item);
            const x = self.filteredItems
            .reduce(
              (accumulator: any[], current: { id: any; }) => accumulator.some(x => x.id === current.id)? accumulator: [...accumulator, current ], []
            )
            self.filteredItems = x;
          }

          return itemStatus;
        })

        for (let i = 0; i < 5; i++) {
          if (self.dataList.length < self.filteredItems.length) {
            self.dataList.push(self.filteredItems[self.dataList.length]);
          }
        }

        this.errorMsg();
        this.loading = false;
      })
    ).subscribe();

  }

  // Get gig detail
  gigDetail(id: string, grouping: string, classification: string, slug: string) {
    this.id = id;
    let groupslug = this.slugify.transform(grouping);
    let classlug = this.slugify.transform(classification);
    this.slug = slug;
    // this.router.navigate([this.detailUrl + '/' + groupslug + '/' + classlug + '/' + this.slug ]);
    this.router.navigate([this.detailUrl + '/detail/' + this.slug ]);
  }

  clearFilters(e: any) {
    this.filtersObj = e;
    this.searchValue = null;
    this.initItems('');
  }

  // Get Filter value
  getFilters(e: any) {
    this.filtersObj = e;
    this.searchValue = null;
    this.initItems('');
  }

  // Get search keyword
  getSearch(elem: any) {
    // set key to the value of the elem
    let key = elem.srcElement.value;
    let loadSearch = false;

    if (key == null) {
      loadSearch = true;
    } else if ((key.length >= this.searchMin) || (key == '')) {
      loadSearch = true;
    }

    // Quick search will start
    if (loadSearch ) {
      this.searchValue = key;
      this.initItems(key);
    }
  }

  // Search autocomplete Filter
  searchFilter(key: string, item: { title: string; overview: string; }) {
    let searchBoolean;

    // Search filter
    if (key) {
      if(item.title && key) {
        if ( (item.title.toLowerCase().indexOf(key.toLowerCase()) > -1) || item.overview.toLowerCase().indexOf(key.toLowerCase()) > -1) {
          searchBoolean = true;
        }
      }
    } else {
      searchBoolean = true;
    }

    return searchBoolean;
  }

  errorMsg() {
    if (this.filteredItems.length == 0) {
      this.errorMessage = true;
    } else if (this.filteredItems.length > 0) {
      this.errorMessage = false;
    }
  }

  // infinite scroll
  loadData(event: any) {
    // App logic to determine if all data is loaded and disable the infinite scroll
    if (this.dataList.length == this.filteredItems.length) {
      event.target.disabled = true;
    }

    setTimeout(() => {
      for (let i = 0; i < 5; i++) {
        if (this.dataList.length < this.filteredItems.length) {
          this.dataList.push(this.filteredItems[this.dataList.length]);
        }
      }
      event.target.complete();
    }, 500);
  }
}
